import type { AuthUser } from "./types";

/**
 * Cross-tab sync. The access token is per-tab in-memory state, so without
 * this, opening a second tab (or a background tab silently refreshing)
 * would leave sibling tabs unaware a rotation/logout just happened --
 * relaying the *result* here (not re-deriving it) is what "multiple tabs"
 * needs, since the refresh-token cookie itself is invisible to JS anyway.
 */

type AuthBroadcastMessage =
  | { type: "session"; user: AuthUser; accessToken: string }
  | { type: "logout" };

const CHANNEL_NAME = "arteria-auth";

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;

export const broadcastSession = (user: AuthUser, accessToken: string): void => {
  channel?.postMessage({ type: "session", user, accessToken } satisfies AuthBroadcastMessage);
};

export const broadcastLogout = (): void => {
  channel?.postMessage({ type: "logout" } satisfies AuthBroadcastMessage);
};

export const onAuthBroadcast = (
  handler: (msg: AuthBroadcastMessage) => void,
): (() => void) => {
  if (!channel) return () => {};
  const listener = (e: MessageEvent<AuthBroadcastMessage>) => handler(e.data);
  channel.addEventListener("message", listener);
  return () => channel.removeEventListener("message", listener);
};
